import { useEffect, useState, useContext } from "react";
import { Box } from "@mui/material";
import { CurrentUserContext } from "../components/context/RequireAuth";
import {
  ListEventsRequest,
  ListEventsResponse_EventCalendar,
  MemberCalendarSearchRequest,
} from "protogen/calendar_service_pb";
import { EventNotice, EventNotice_NoticeType } from "protogen/calendar_pb";
import {
  useListMemberCalendarEvents,
  useRefreshCalendars,
  useGetMemberCalendarIntegrations,
  useMemberCalendarSearch,
} from "services/calendar";
import FlexPage from "components/layout/FlexPage";
import { defaultParametersV2, CalendarParametersV2 } from "types/calendars";
import { CalendarFilter } from "components/calendar/CustomCalendarSettings";
import FayeCalendar from "components/calendar/FayeCalendar";
import {
  createColorMap,
  defaultColor,
  deduplicate,
  getWeekBoundaryInfo,
} from "components/calendar/utils";
import useIsMobile from "components/hooks/useIsMobile";
import { MemberCalendarTodos } from "components/calendar/CalendarTodos";
import useDateParams from "components/hooks/calendar/useDateParams";
import useTitle from "components/hooks/useTitle";

const FAYE_CALENDAR = "_faye-events";

export default () => {
  useTitle("Calendar");
  const isMobile = useIsMobile();
  const defaultDate = useDateParams();
  const currentUser = useContext(CurrentUserContext);
  // For member calendar: only show user's timezone from settings, no timezone controls
  const viewTimezone =
    currentUser?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [enabledCalendars, setEnabledCalendars] = useState<
    CalendarFilter[] | undefined
  >(undefined);
  const [calendarColorMap, setCalendarColorMap] = useState<Record<
    string,
    [string, string]
  > | null>(null);
  const [events, setEvents] = useState<EventNotice[]>([]);
  const [params, setParams] = useState<CalendarParametersV2>({
    ...defaultParametersV2(),
  });
  const [memberCalendars, setMemberCalendars] = useState<
    ListEventsResponse_EventCalendar[] | null
  >();
  const { request: memberEventsRequest, loading } = useListMemberCalendarEvents(
    (r) => {
      if (!calendarColorMap) {
        setCalendarColorMap(
          createColorMap(
            (r?.calendars || []).map(
              (c) => `${c.integrationRef}|${c.calendarId}`,
            ),
          ),
        );
      }
      if (!memberCalendars) {
        setMemberCalendars(r?.calendars || []);
      }
    },
  );
  const { request: refreshRequest } = useRefreshCalendars();
  const { request: getIntegrations } = useGetMemberCalendarIntegrations();
  const { request: memberCalendarSearchRequest } = useMemberCalendarSearch();

  useEffect(() => {
    fetchEvents(defaultDate);
    getIntegrations();
  }, []);

  const fetchEvents = async (newDate: Date) => {
    const { currentMonth, currentYear, otherMonth, otherYear } =
      getWeekBoundaryInfo(newDate);

    setParams({
      ...params,
      month: currentMonth,
      year: currentYear,
      day: newDate.getDate(),
    });

    const requests = [
      memberEventsRequest(
        new ListEventsRequest({
          ...params,
          familyRefs: undefined,
          month: currentMonth,
          year: currentYear,
          includeExternalEvents: true,
        }),
      ),
    ];

    if (otherMonth !== null && otherYear !== null) {
      requests.push(
        memberEventsRequest(
          new ListEventsRequest({
            ...params,
            familyRefs: undefined,
            month: otherMonth,
            year: otherYear,
            includeExternalEvents: true,
          }),
        ),
      );
    }
    const results = await Promise.all(requests);
    // A basic dedupe on eventRef and startSec.  The FE requests month/year,
    // but the backend request an extra day window on each end to account for
    // timezone.  A more robust API that accepts more granular time or other improvements
    // can remove this dedupe.
    const allEvents = deduplicate(
      // @ts-ignore
      results.flatMap((result) => result?.eventNotices || []),
      ["eventRef", "startSec"],
    );

    setEvents(allEvents);
  };
  return (
    <FlexPage
      leftAligned
      fullHeight
      headerPadding={false}
      sx={{
        padding: isMobile ? "16px 6px 0" : "16px 32px 0",
        maxWidth: "1400px",
      }}
    >
      {/* TODO: If at least one of the todos is not done, then... */}
      <Box
        sx={{
          display: "flex",
          height: "100%",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <MemberCalendarTodos />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "10px",
            flexGrow: 1,
            padding: isMobile ? "0 20px" : "",
          }}
        >
          <FayeCalendar
            accountType="member"
            events={events.filter((e) => {
              if (!enabledCalendars) {
                return true;
              }
              const calSet = new Set(enabledCalendars?.map((c) => c.key) || []);
              if (
                e.noticeType ===
                EventNotice_NoticeType.NoticeType_EXTERNAL_EVENT
              ) {
                return calSet.has(
                  `${e.externalCalendarRef}|${e.externalCalendarId}`,
                );
              }
              return calSet.has(FAYE_CALENDAR);
            })}
            getEventStyles={(event) => {
              const colors =
                calendarColorMap?.[
                  `${event.externalCalendarRef}|${event.externalCalendarId}`
                ];
              return {
                backgroundColor: colors?.[0] || defaultColor[0],
                color: colors?.[1] || defaultColor[1],
              };
            }}
            calendarFilters={[
              ...(memberCalendars || [])
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((c) => ({
                  key: `${c.integrationRef}|${c.calendarId}`,
                  name: c.name,
                  color:
                    calendarColorMap?.[
                      `${c.integrationRef}|${c.calendarId}`
                    ][0],
                })),
              {
                name: "Faye Events",
                key: FAYE_CALENDAR,
              },
            ]}
            setEnabledFilters={(filters) => {
              setEnabledCalendars(filters);
            }}
            refreshEvents={async () => {
              fetchEvents(new Date(params.year, params.month - 1, params.day));
            }}
            refreshCalendar={async () => {
              await refreshRequest({
                familyRef: "",
              });
            }}
            handleNavigate={async (d) => {
              fetchEvents(d);
            }}
            loading={loading}
            viewTimezone={viewTimezone}
            onTimezoneChange={undefined}
            secondaryTimezone={undefined}
            dualTimezoneEnabled={false}
            onDualTimezoneChange={undefined}
            searchEvents={async (query) => {
              const enabledFilterKeys = new Set(
                (enabledCalendars || []).map((calendar) => calendar.key),
              );
              if (enabledCalendars && !enabledFilterKeys.has(FAYE_CALENDAR)) {
                return [];
              }
              const response = await memberCalendarSearchRequest(
                new MemberCalendarSearchRequest({ query, limit: 100 }),
              );
              return response?.eventNotices || [];
            }}
          />
        </Box>
      </Box>
    </FlexPage>
  );
};
